import { useEffect, useState } from 'react';
import type { NumberField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';

/**
 * Number input. Holds the raw text locally (so a half-typed "-" / "" doesn't fight the user), commits a
 * parsed+clamped number on change, rejects NaN. Clamps again on blur (build-plan §6.3).
 */
export function NumberControl({
  field,
  value,
  onChange,
}: {
  field: NumberField;
  value: number | undefined;
  onChange: (next: number) => void;
}) {
  const [text, setText] = useState(value !== undefined ? String(value) : '');
  useEffect(() => setText(value !== undefined ? String(value) : ''), [value]);

  const clamp = (n: number) => {
    let v = n;
    if (field.min !== undefined) v = Math.max(field.min, v);
    if (field.max !== undefined) v = Math.min(field.max, v);
    return v;
  };

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} layout="row">
      <div className="bld-number">
        <input
          id={field.id}
          className="bld-input"
          type="number"
          value={text}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => {
            setText(e.target.value);
            const n = Number(e.target.value);
            if (e.target.value !== '' && !Number.isNaN(n)) onChange(clamp(n));
          }}
          onBlur={() => {
            const n = Number(text);
            if (text === '' || Number.isNaN(n)) {
              setText(value !== undefined ? String(value) : '');
            } else {
              const c = clamp(n);
              setText(String(c));
              onChange(c);
            }
          }}
        />
        {field.unit && <span className="bld-number__unit">{field.unit}</span>}
      </div>
    </FieldRow>
  );
}
